import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";

import { TabPanel } from "@mui/lab";
import { Stack, SxProps } from "@mui/material";

import LabeledSpinner from "@/components/LabeledSpinner";
import { LIST_MAKER } from "@/components/ListTabs";
import TabbedSidebar from "@/components/TabbedSidebar";

import { Case } from "@/extensions/klarahill/types";
import { ObituaryDialogContextProvider } from "@/extensions/obituary/contexts/ObituaryDialogContext";
import usePreviewQuery from "@/extensions/obituary/queries/usePreviewQuery";
import { Ad, UpdateAdPayload } from "@/extensions/obituary/types";
import adName from "@/extensions/obituary/utils/adName";

import useDebounce from "@/hooks/useDebounce";
import useHash from "@/hooks/useHash";

import AdPanel from "./AdPanel";
import Canvas from "./Canvas";
import reducer from "./reducer";
import { EditPanel, Story } from "./Story";

import { Nullish, Option } from "@/utils/types";

const styles: Record<string, SxProps> = {
  wrapper: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  tabPanel: { p: 0, height: "100%" },
  spinner: { width: "100%", height: "100%" },
};

const sidebarTabs = [
  { label: "Innehåll", value: "editor" },
  { label: "Annons", value: "ad" },
];

export interface ObituaryEditorProps {
  c: Case;
  ad: Ad;
  ads: Ad[];
  setAd: (adId: Ad["id"]) => void;
  updateAd: (data: {
    ad: Nullish<Ad>;
    story: Nullish<Story>;
    payload?: Nullish<Partial<UpdateAdPayload>>;
  }) => void;
}

const ObituaryEditor: React.FC<ObituaryEditorProps> = ({
  c,
  ad,
  ads,
  setAd,
  updateAd,
}) => {
  const [_, setSearchParams] = useSearchParams();

  const [story, dispatch] = useReducer(reducer, ad.story);

  // Use a hash to detect changes in the story, prevent unneccary update changes.
  const storyHash = useHash(story);

  // Debounce the storyHash to account for multiple field changes into a single preview fetch
  const [debouncedStoryHash, setDebouncedStoryHash] = React.useState(storyHash);
  const debouncedSetHash = useDebounce(setDebouncedStoryHash, 750);

  useEffect(() => {
    debouncedSetHash(storyHash);
  }, [storyHash, debouncedSetHash]);

  const previousStoryHash = useRef<string>();
  useEffect(() => {
    if (debouncedStoryHash === previousStoryHash.current) return;
    updateAd({ ad, story });
    previousStoryHash.current = debouncedStoryHash;
  }, [ad, story, debouncedStoryHash]);

  // Get the preview image based on the current story.
  const previewPayload = useMemo(() => {
    return {
      publication: ad.publication.abbreviation,
      layout: ad.layout_variant,
      story,
    };
  }, [ad.publication.abbreviation, ad.layout_variant, story]);

  const { preview, lastPreview } = usePreviewQuery(
    ad.id,
    debouncedStoryHash,
    previewPayload,
  );

  const showCanvas = useMemo(() => {
    return ad != null && preview != null && lastPreview !== "";
  }, [ad, preview]);

  const tabs = useMemo<Option[]>(() => {
    if (!Array.isArray(ads)) return [];
    return ads.map((ad) => ({ label: adName(ad), value: ad.id.toString() }));
  }, [ads]);

  const handleTabChange = useCallback(({ value }: Option) => {
    if (value === LIST_MAKER) {
      setSearchParams({ tab: "obituary.ad:list" });
    } else {
      const newAdId = Number(value);
      if (Number.isNaN(newAdId)) throw new Error("Invalid ad id");
      setAd(newAdId);
    }
  }, []);

  const handleAdPanelUpdateAd = useCallback(
    (payload: Partial<UpdateAdPayload>) => {
      updateAd({ payload, ad, story });
    },
    [ad, story, updateAd],
  );

  const handleAdCreate = useCallback((ad: Ad) => {
    const adId = ad.id.toString();
    setSearchParams({ tab: "obituary.ad:list", adId });
  }, []);

  return (
    <ObituaryDialogContextProvider
      key={ad.id}
      ads={ads}
      c={c}
      onAdCreate={handleAdCreate}
    >
      <Stack sx={{ height: "100%", position: "relative" }}>
        <Stack direction="row" spacing={2} sx={styles.wrapper}>
          <TabbedSidebar tabs={sidebarTabs}>
            <TabPanel sx={styles.tabPanel} value="editor">
              <EditPanel
                c={c}
                dispatch={dispatch}
                layout={ad.layout_variant}
                story={story}
              />
            </TabPanel>
            <TabPanel value="ad">
              <AdPanel ad={ad} updateAd={handleAdPanelUpdateAd} />
            </TabPanel>
          </TabbedSidebar>
          {showCanvas ? (
            <Canvas
              adId={ad.id}
              layout={ad.layout_variant}
              src={lastPreview!}
              tabs={tabs}
              onTabChange={handleTabChange}
            />
          ) : (
            <LabeledSpinner
              label="Laddar förhandsvisning..."
              sx={styles.spinner}
            />
          )}
        </Stack>
      </Stack>
    </ObituaryDialogContextProvider>
  );
};

export default memo(ObituaryEditor);
